import { forwardRef, useEffect, useImperativeHandle, useRef, useState, type CSSProperties } from 'react';
import { upperMantendoCursor } from '../../components/inputs/UpperInput';
import { Search, Trash2, UserPlus, X as XIcon, AlertTriangle } from 'lucide-react';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { useToast } from '../../components/Toast';
import { useDialog } from '../../components/DialogProvider';
import { MSG_PRODUTO_INATIVO, produtoInativo, SeloInativo } from '../../lib/produto-inativo';
import { ModalBuscarCliente } from '../../components/orcamento/ModalBuscarCliente';
import { ModalReciboTroca, type ReciboTrocaInfo } from '../../components/trocas/ModalReciboTroca';
import { ComboBox } from '../../components/ComboBox';
import { formatMoeda, formatDataHoraSP } from '@fiodecobre/shared';

export interface NovaTrocaHandle {
  limpar: () => void;
  finalizar: () => void;
  /** As opcoes Devolucao/Troca/Compra moram no cabecalho da pagina (TrocasHome) desde 22/10/2026. */
  escolherTipo: (t: TipoTroca) => void;
}

export interface NovaTrocaEstado {
  bloqueio: string | null;
  salvando: boolean;
  valorDevolvido: number;
  valorLevado: number;
  saldoCredito: number;
  /** Preenchido so na Compra com Credito: o painel do topo mostra orcamento x credito no lugar de devolvido x levado. */
  compra?: { total: number; credito: number } | null;
  tipo: TipoTroca;
  /** Troca (leva outros) so pra admin/gerente/estoquista — o cabecalho esconde a opcao dos demais. */
  podeTrocar: boolean;
}

export type TipoTroca = 'troca' | 'devolucao' | 'compra_credito';

// Compra com Credito (migration 0340): o que GET /api/trocas/compra-credito/orcamentos devolve.
// O cliente vem do orcamento (pedido dele, 22/10/2026) — nao ha campo de cliente neste modo.
interface OrcamentoCompra {
  id: number;
  numero: string;
  total: string;
  criado_em: string;
  vendedor_nome: string | null;
  cliente_id: number | null;
  cliente_nome: string;
  cliente_codigo: string | null;
}

// Itens do orcamento escolhido — vem de rota propria, so do escolhido (a lista traz ~2 mil orcamentos).
interface ItemOrcamentoCompra {
  produto_id: number;
  codigo_interno: string | null;
  produto_descricao: string;
  foto_url: string | null;
  quantidade: string;
  preco_unitario: string;
  subtotal: string;
  estoque_atual: string;
}

interface CreditoCompra {
  id: number;
  cliente_id: number;
  numero: string;
  valor_original: string;
  saldo: string;
  criado_em: string;
  troca_numero: string | null;
}

interface ProdutoSugestao {
  id: number;
  codigo_interno: string | null;
  descricao: string;
  foto_url: string | null;
  estoque_atual: number | string | null;
  preco_pix: number | string | null;
  status?: string | null;
}

interface ClienteSel {
  id: number;
  nome: string;
  codigo_legado: string | null;
  cpf_cnpj: string | null;
}

interface ItemTroca {
  direcao: 'devolvido' | 'levado';
  produto_id: number;
  codigo_interno: string | null;
  descricao: string;
  foto_url: string | null;
  quantidade: number;
  preco_unitario: number;
  subtotal: number;
  estoque_atual: number | null;
}

// Replica backend: devolvido e levado usam o preco_pix cheio do produto (2 casas).
function precoPorDirecao(precoPix: number, _direcao: 'devolvido' | 'levado'): number {
  return Math.round(precoPix * 100) / 100;
}

interface Props {
  onConcluida: () => void;
  onEstadoChange?: (e: NovaTrocaEstado) => void;
  /** Quando setado, carrega a troca retornada para correcao (PUT em vez de POST). */
  editarId?: number | null;
}

interface TrocaParaEditar {
  cliente_id: number;
  cliente_nome: string;
  cliente_codigo: string | null;
  tipo: 'troca' | 'devolucao';
  gerar_credito: boolean;
  observacoes: string | null;
  itens: Array<{
    direcao: 'devolvido' | 'levado';
    produto_id: number;
    codigo_interno: string | null;
    produto_descricao: string;
    foto_url: string | null;
    quantidade: string;
    preco_unitario: string;
    subtotal: string;
  }>;
}

const RASCUNHO_KEY = 'trocas.nova.rascunho';

interface Rascunho {
  cliente: ClienteSel | null;
  tipo: TipoTroca;
  itens: ItemTroca[];
  gerarCredito: boolean;
  observacoes: string;
}

function carregarRascunho(): Rascunho | null {
  try {
    const raw = localStorage.getItem(RASCUNHO_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as Rascunho;
  } catch { return null; }
}

export const NovaTroca = forwardRef<NovaTrocaHandle, Props>(function NovaTroca({ onConcluida, onEstadoChange, editarId }, ref) {
  const toast = useToast();
  const dialog = useDialog();
  const { usuario } = useAuth();
  // Troca (devolve e leva outros — mercadoria sai da loja) e restrita: admin/gerente/estoquista
  // escolhem. Vendedora faz so Devolucao (default), que apenas gera credito e nao tira mercadoria.
  // Espelha `podeEscolherTroca` do trocas.routes.ts — mudou aqui, mudar la (o backend e quem manda).
  const podeTrocar = usuario?.perfil === 'admin' || usuario?.perfil === 'gerente' || usuario?.perfil === 'estoquista';
  // Em modo correcao a fonte e a troca no banco (GET abaixo), nunca o rascunho local.
  const rascunhoInicial = editarId ? null : carregarRascunho();

  const [cliente, setCliente] = useState<ClienteSel | null>(rascunhoInicial?.cliente ?? null);
  const [showModalCliente, setShowModalCliente] = useState(false);

  const [tipo, setTipo] = useState<TipoTroca>(rascunhoInicial?.tipo ?? 'devolucao');
  const ehCompra = tipo === 'compra_credito';

  // Compra com Credito: orcamentos abertos de clientes com credito de troca + os creditos deles.
  // Carrega ao entrar no modo; a escolha do orcamento nao vai pro rascunho (o orcamento muda).
  const [orcamentosCompra, setOrcamentosCompra] = useState<OrcamentoCompra[]>([]);
  const [creditosCompra, setCreditosCompra] = useState<CreditoCompra[]>([]);
  const [orcSelId, setOrcSelId] = useState<number | null>(null);
  const [carregandoCompra, setCarregandoCompra] = useState(false);
  const [erroCompra, setErroCompra] = useState<string | null>(null);
  const [reciboCompra, setReciboCompra] = useState<ReciboTrocaInfo | null>(null);
  const [itensSel, setItensSel] = useState<ItemOrcamentoCompra[]>([]);
  const [carregandoItens, setCarregandoItens] = useState(false);

  useEffect(() => {
    setItensSel([]);
    if (!orcSelId) return;
    let cancelado = false;
    setCarregandoItens(true);
    (async () => {
      try {
        const r = await api.get<ItemOrcamentoCompra[]>(`/api/trocas/compra-credito/orcamentos/${orcSelId}/itens`);
        if (!cancelado) setItensSel(r);
      } catch (e: any) {
        if (!cancelado) toast.erro(e?.message || 'Nao consegui carregar os itens do orcamento');
      } finally {
        if (!cancelado) setCarregandoItens(false);
      }
    })();
    return () => { cancelado = true; };
  }, [orcSelId]); // eslint-disable-line react-hooks/exhaustive-deps
  const [itens, setItens] = useState<ItemTroca[]>(rascunhoInicial?.itens ?? []);

  const [buscaDevolvido, setBuscaDevolvido] = useState('');
  const [buscaLevado, setBuscaLevado] = useState('');
  const [sugDevolvido, setSugDevolvido] = useState<ProdutoSugestao[]>([]);
  const [sugLevado, setSugLevado] = useState<ProdutoSugestao[]>([]);
  const [showSugDevolvido, setShowSugDevolvido] = useState(false);
  const [showSugLevado, setShowSugLevado] = useState(false);

  const [gerarCredito, setGerarCredito] = useState(rascunhoInicial?.gerarCredito ?? true);
  const [observacoes, setObservacoes] = useState(rascunhoInicial?.observacoes ?? '');
  const [salvando, setSalvando] = useState(false);

  // Persiste rascunho a cada mudanca relevante.
  // Limpa quando nao tem nada preenchido — evita ressuscitar entradas vazias.
  // Em modo correcao (editarId) NAO persiste — pra nao vazar a edicao pro rascunho normal.
  useEffect(() => {
    if (editarId) return;
    const temConteudo = !!cliente || itens.length > 0 || observacoes.trim();
    if (!temConteudo) {
      localStorage.removeItem(RASCUNHO_KEY);
      return;
    }
    const r: Rascunho = { cliente, tipo, itens, gerarCredito, observacoes };
    try { localStorage.setItem(RASCUNHO_KEY, JSON.stringify(r)); } catch {}
  }, [cliente, tipo, itens, gerarCredito, observacoes, editarId]);

  // Modo correcao: carrega a troca retornada e preenche o formulario.
  useEffect(() => {
    if (!editarId) return;
    let cancelado = false;
    (async () => {
      try {
        const d = await api.get<TrocaParaEditar>(`/api/trocas/${editarId}`);
        if (cancelado) return;
        setCliente({ id: d.cliente_id, nome: d.cliente_nome, codigo_legado: d.cliente_codigo, cpf_cnpj: null });
        setTipo(d.tipo);
        setGerarCredito(d.gerar_credito);
        setObservacoes(d.observacoes ?? '');
        setItens(d.itens.map((i) => ({
          direcao: i.direcao,
          produto_id: i.produto_id,
          codigo_interno: i.codigo_interno,
          descricao: i.produto_descricao,
          foto_url: i.foto_url,
          quantidade: Number(i.quantidade),
          preco_unitario: Number(i.preco_unitario),
          subtotal: Number(i.subtotal),
          estoque_atual: null,
        })));
      } catch (e: any) {
        if (!cancelado) toast.erro(e.message || 'Erro ao carregar troca para correcao');
      }
    })();
    return () => { cancelado = true; };
  }, [editarId]); // eslint-disable-line react-hooks/exhaustive-deps

  async function carregarCompra() {
    setCarregandoCompra(true);
    setErroCompra(null);
    try {
      const r = await api.get<{ orcamentos: OrcamentoCompra[]; creditos: CreditoCompra[] }>(
        '/api/trocas/compra-credito/orcamentos',
      );
      setOrcamentosCompra(r.orcamentos);
      setCreditosCompra(r.creditos);
      // Escolha antiga que sumiu da lista (foi pro caixa, foi levada) nao pode ficar marcada.
      setOrcSelId((atual) => (atual && r.orcamentos.some((o) => o.id === atual) ? atual : null));
    } catch (e: any) {
      setOrcamentosCompra([]);
      setCreditosCompra([]);
      setErroCompra(e?.message || 'Nao consegui carregar os orcamentos e creditos');
    } finally {
      setCarregandoCompra(false);
    }
  }

  useEffect(() => {
    if (!ehCompra) {
      setOrcamentosCompra([]);
      setCreditosCompra([]);
      setOrcSelId(null);
      setErroCompra(null);
      return;
    }
    void carregarCompra();
  }, [ehCompra]); // eslint-disable-line react-hooks/exhaustive-deps

  const inputDevolvidoRef = useRef<HTMLInputElement>(null);
  const inputLevadoRef = useRef<HTMLInputElement>(null);

  // Busca produtos para cada direcao independente
  useEffect(() => {
    if (buscaDevolvido.trim().length < 2) { setSugDevolvido([]); return; }
    const t = setTimeout(async () => {
      try {
        const r = await api.get<ProdutoSugestao[]>('/api/produtos', { query: { q: buscaDevolvido, com_inativos: '1' } });
        setSugDevolvido(r);
        setShowSugDevolvido(true);
      } catch {}
    }, 250);
    return () => clearTimeout(t);
  }, [buscaDevolvido]);

  useEffect(() => {
    if (buscaLevado.trim().length < 2) { setSugLevado([]); return; }
    const t = setTimeout(async () => {
      try {
        const r = await api.get<ProdutoSugestao[]>('/api/produtos', { query: { q: buscaLevado, com_inativos: '1' } });
        setSugLevado(r);
        setShowSugLevado(true);
      } catch {}
    }, 250);
    return () => clearTimeout(t);
  }, [buscaLevado]);

  // Fecha sugestoes ao clicar fora
  useEffect(() => {
    function onClick(e: MouseEvent) {
      const t = e.target as Node;
      if (!inputDevolvidoRef.current?.parentElement?.contains(t)) setShowSugDevolvido(false);
      if (!inputLevadoRef.current?.parentElement?.contains(t)) setShowSugLevado(false);
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  function adicionarProduto(direcao: 'devolvido' | 'levado', prod: ProdutoSugestao) {
    if (produtoInativo(prod)) { void dialog.alerta(MSG_PRODUTO_INATIVO); return; }
    const precoPix = Number(prod.preco_pix ?? 0);
    if (precoPix <= 0) {
      toast.erro(`Produto "${prod.descricao}" sem preco_pix cadastrado`);
      return;
    }
    const estoque = prod.estoque_atual != null ? Number(prod.estoque_atual) : null;
    const preco = precoPorDirecao(precoPix, direcao);

    setItens((prev) => {
      const idx = prev.findIndex((i) => i.direcao === direcao && i.produto_id === prod.id);
      if (idx >= 0) {
        const novaQtd = prev[idx]!.quantidade + 1;
        if (direcao === 'levado' && estoque != null && novaQtd > estoque) {
          toast.erro(`Estoque insuficiente: ${prod.descricao} (estoque ${estoque})`);
          return prev;
        }
        const next = [...prev];
        next[idx] = {
          ...next[idx]!,
          quantidade: novaQtd,
          subtotal: Math.round(preco * novaQtd * 100) / 100,
        };
        return next;
      }
      if (direcao === 'levado' && estoque != null && estoque < 1) {
        toast.erro(`Sem estoque: ${prod.descricao}`);
        return prev;
      }
      return [...prev, {
        direcao,
        produto_id: prod.id,
        codigo_interno: prod.codigo_interno,
        descricao: prod.descricao,
        foto_url: prod.foto_url,
        quantidade: 1,
        preco_unitario: preco,
        subtotal: preco,
        estoque_atual: estoque,
      }];
    });

    if (direcao === 'devolvido') {
      setBuscaDevolvido('');
      setShowSugDevolvido(false);
      setTimeout(() => inputDevolvidoRef.current?.focus(), 50);
    } else {
      setBuscaLevado('');
      setShowSugLevado(false);
      setTimeout(() => inputLevadoRef.current?.focus(), 50);
    }
  }

  function mudarQtd(idx: number, novaQtd: number) {
    if (novaQtd <= 0) return;
    setItens((prev) => {
      const it = prev[idx]!;
      if (it.direcao === 'levado' && it.estoque_atual != null && novaQtd > it.estoque_atual) {
        toast.erro(`Estoque insuficiente: ${it.descricao} (estoque ${it.estoque_atual})`);
        return prev;
      }
      const next = [...prev];
      next[idx] = { ...it, quantidade: novaQtd, subtotal: Math.round(it.preco_unitario * novaQtd * 100) / 100 };
      return next;
    });
  }

  function removerItem(idx: number) {
    setItens((prev) => prev.filter((_, i) => i !== idx));
  }

  // Quando muda para 'devolucao' remove itens levados
  useEffect(() => {
    if (tipo === 'devolucao' && itens.some((i) => i.direcao === 'levado')) {
      setItens((prev) => prev.filter((i) => i.direcao !== 'levado'));
    }
  }, [tipo]); // eslint-disable-line react-hooks/exhaustive-deps

  // Operador comum nao pode ficar preso em 'troca' via rascunho antigo (opcao nem aparece).
  useEffect(() => {
    if (!podeTrocar && tipo === 'troca') setTipo('devolucao');
  }, [podeTrocar, tipo]);

  const devolvidos = itens.filter((i) => i.direcao === 'devolvido');
  const levados = itens.filter((i) => i.direcao === 'levado');
  const valorDevolvido = devolvidos.reduce((s, i) => s + i.subtotal, 0);
  const valorLevado = levados.reduce((s, i) => s + i.subtotal, 0);
  const saldoCredito = Math.round((valorDevolvido - valorLevado) * 100) / 100;

  const orcSel = orcamentosCompra.find((o) => o.id === orcSelId) ?? null;
  const totalCompra = orcSel ? Number(orcSel.total) : 0;
  // Creditos do cliente DO ORCAMENTO escolhido; sem orcamento, nenhum.
  const creditosDoCliente = orcSel ? creditosCompra.filter((c) => c.cliente_id === orcSel.cliente_id) : [];
  const creditoDisponivel = Math.round(creditosDoCliente.reduce((s, c) => s + Number(c.saldo), 0) * 100) / 100;
  const sobraCredito = Math.round((creditoDisponivel - totalCompra) * 100) / 100;

  // Regras da Compra com Credito (respostas dele, 22/10/2026). O servidor repete todas.
  function bloqueioCompra(): string | null {
    if (carregandoCompra) return 'Carregando orcamentos e creditos...';
    if (erroCompra) return erroCompra;
    if (orcamentosCompra.length === 0) return 'Nenhum orcamento aberto de cliente com credito. O orcamento precisa estar no nome do cliente e fora do Caixa.';
    if (!orcSel) return 'Escolha o orcamento';
    if (!orcSel.cliente_id || orcSel.cliente_id === 1) return 'Este orcamento esta em Consumidor — coloque no nome do cliente para usar o credito dele';
    if (carregandoItens) return 'Carregando itens do orcamento...';
    if (creditoDisponivel <= 0) return 'Este cliente nao tem credito de Trocas e Devolucoes';
    if (Math.round(totalCompra * 100) > Math.round(creditoDisponivel * 100)) {
      return `O credito nao cobre o orcamento — faltam ${formatMoeda(totalCompra - creditoDisponivel)}. Esta opcao so aceita credito da loja.`;
    }
    const semEstoque = itensSel.filter((i) => Number(i.estoque_atual) < Number(i.quantidade));
    if (semEstoque.length) return `Estoque insuficiente: ${semEstoque.map((i) => i.produto_descricao).join(', ')}`;
    return null;
  }

  function podeFinalizar(): string | null {
    if (ehCompra) return bloqueioCompra();
    if (!cliente) return 'Selecione o cliente';
    if (cliente.id === 1) return 'Cliente Consumidor nao aceita troca — cadastre o cliente';
    if (itens.length === 0) return 'Adicione ao menos 1 produto';
    if (tipo === 'troca') {
      if (devolvidos.length === 0) return 'Troca exige pelo menos 1 item devolvido';
      if (levados.length === 0) return 'Troca exige pelo menos 1 item levado';
    }
    return null;
  }

  async function finalizarCompra() {
    if (!orcSel) return;
    setSalvando(true);
    try {
      type Resp = { ok: boolean; compra: { id: number; numero: string; orcamento_numero: string; valor: number; validada: boolean } };
      const r = await api.post<Resp>('/api/trocas/compra-credito', {
        orcamento_id: orcSel.id,
        observacoes: observacoes.trim() || undefined,
      });
      let msg = `Compra com credito ${r.compra.numero} registrada — orcamento ${r.compra.orcamento_numero}`;
      if (!r.compra.validada) msg += ' (aguarda validacao do gerente)';
      toast.sucesso(msg);
      localStorage.removeItem(RASCUNHO_KEY);
      // Na finalizacao, a opcao de imprimir o comprovante (pedido dele). Fechar o comprovante leva ao Historico.
      setReciboCompra({
        id: r.compra.id,
        numero: r.compra.numero,
        clienteNome: orcSel.cliente_nome,
        clienteCodigo: orcSel.cliente_codigo,
        saldoCredito: sobraCredito,
        rotuloSaldo: 'Credito restante:',
        creditoNumero: null,
        clienteWhatsapp: null,
      });
    } catch (e: any) {
      toast.erro(e.message || 'Erro ao registrar a compra com credito');
      // O orcamento pode ter mudado (foi pro caixa, outra pessoa levou) — relê a lista.
      void carregarCompra();
    } finally {
      setSalvando(false);
    }
  }

  async function finalizar() {
    const err = podeFinalizar();
    if (err) { toast.erro(err); return; }
    if (ehCompra) { await finalizarCompra(); return; }

    setSalvando(true);
    try {
      const payload = {
        cliente_id: cliente!.id,
        tipo,
        gerar_credito: gerarCredito && saldoCredito > 0,
        observacoes: observacoes.trim() || undefined,
        itens: itens.map((i) => ({ direcao: i.direcao, produto_id: i.produto_id, quantidade: i.quantidade })),
      };
      type Resp = { ok: boolean; troca: { id: number; numero: string; saldo_credito: number; validada: boolean; credito: { numero: string } | null } };
      const r = editarId
        ? await api.put<Resp>(`/api/trocas/${editarId}`, payload)
        : await api.post<Resp>('/api/trocas', payload);
      let msg = editarId ? `Troca ${r.troca.numero} corrigida` : `Troca ${r.troca.numero} registrada`;
      if (r.troca.credito) msg += ` — credito ${r.troca.credito.numero} de ${formatMoeda(r.troca.saldo_credito)}`;
      if (!r.troca.validada) msg += ' (aguarda validacao do gerente)';
      toast.sucesso(msg);
      // Reset + limpa rascunho
      localStorage.removeItem(RASCUNHO_KEY);
      setCliente(null);
      setTipo('devolucao');
      setItens([]);
      setObservacoes('');
      setGerarCredito(true);
      onConcluida();
    } catch (e: any) {
      toast.erro(e.message || 'Erro ao registrar troca');
    } finally {
      setSalvando(false);
    }
  }

  function limpar() {
    localStorage.removeItem(RASCUNHO_KEY);
    setCliente(null);
    setTipo('devolucao');
    setItens([]);
    setObservacoes('');
    setGerarCredito(true);
  }

  useImperativeHandle(ref, () => ({ limpar, finalizar, escolherTipo: setTipo }));

  // Notifica TrocasHome do estado atual (pra controlar botoes do header + painel de totais)
  const bloqueio = podeFinalizar();
  useEffect(() => {
    onEstadoChange?.({
      bloqueio, salvando, valorDevolvido, valorLevado, saldoCredito,
      compra: ehCompra ? { total: totalCompra, credito: creditoDisponivel } : null,
      tipo, podeTrocar,
    });
  }, [bloqueio, salvando, valorDevolvido, valorLevado, saldoCredito, ehCompra, totalCompra, creditoDisponivel, tipo, podeTrocar, onEstadoChange]);

  function renderSecao(direcao: 'devolvido' | 'levado', visivel: boolean) {
    if (!visivel) return null;
    const lista = direcao === 'devolvido' ? devolvidos : levados;
    const busca = direcao === 'devolvido' ? buscaDevolvido : buscaLevado;
    const setBusca = direcao === 'devolvido' ? setBuscaDevolvido : setBuscaLevado;
    const sug = direcao === 'devolvido' ? sugDevolvido : sugLevado;
    const showSug = direcao === 'devolvido' ? showSugDevolvido : showSugLevado;
    const setShowSug = direcao === 'devolvido' ? setShowSugDevolvido : setShowSugLevado;
    const inputRef = direcao === 'devolvido' ? inputDevolvidoRef : inputLevadoRef;
    const titulo = direcao === 'devolvido' ? 'Itens Devolvidos' : 'Itens Levados';
    const cor = direcao === 'devolvido' ? '#16a34a' : '#2563eb';

    return (
      <div style={{
        background: 'var(--card-bg)',
        border: '1px solid var(--border)', borderRadius: 8, padding: 16,
        display: 'flex', flexDirection: 'column', minHeight: 0,
      }}>
        {/* Linha topo: input de busca a esquerda + titulo a direita */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12, flexShrink: 0 }}>
          <div style={{ position: 'relative', flex: 1 }}>
            <Search size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
            <input
              ref={inputRef}
              type="search"
              placeholder={`Buscar produto pra ${direcao}... (codigo ou descricao)`}
              value={busca}
              onChange={(e) => setBusca(upperMantendoCursor(e))}
              onFocus={() => { if (sug.length > 0) setShowSug(true); }}
              style={{ width: '100%', padding: '8px 12px 8px 32px', border: '1px solid var(--input-border)', borderRadius: 6, background: '#fff', color: 'var(--input-fg)' }}
            />
            {showSug && sug.length > 0 && (
              <div style={{
                position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4,
                background: 'var(--panel-bg)', border: '1px solid var(--border)', borderRadius: 6,
                maxHeight: 280, overflowY: 'auto', zIndex: 30,
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              }}>
                {sug.slice(0, 30).map((p) => (
                  <div
                    key={p.id}
                    onClick={() => adicionarProduto(direcao, p)}
                    style={{ padding: '6px 10px', cursor: 'pointer', borderBottom: '1px solid var(--border)', fontSize: 14, color: '#000', display: 'flex', alignItems: 'center', gap: 8 }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--grid-row-alt-bg)')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                  >
                    {p.foto_url ? <img src={p.foto_url} alt="" style={{ width: 28, height: 28, objectFit: 'contain' }} /> : <div style={{ width: 28, height: 28 }} />}
                    <span style={{ width: 60 }}>{p.codigo_interno || '-'}</span>
                    <span style={{ flex: 1 }}>{produtoInativo(p) && <SeloInativo />}{p.descricao}</span>
                    <span>
                      {formatMoeda(Number(p.preco_pix ?? 0))} · est: {p.estoque_atual ?? '-'}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: cor, whiteSpace: 'nowrap' }}>{titulo}</h3>
        </div>

        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        {lista.length === 0 ? (
          <div style={{ padding: 16, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
            Nenhum item adicionado
          </div>
        ) : (
          <table className="tabela" style={{ width: '100%', fontSize: 14 }}>
            <thead>
              <tr>
                <th style={{ width: 64 }}></th>
                <th style={{ width: 70 }}>Cod</th>
                <th>Produto</th>
                <th style={{ width: 100, textAlign: 'right' }}>Unit.</th>
                <th style={{ width: 90, textAlign: 'center' }}>Qtd</th>
                <th style={{ width: 110, textAlign: 'right' }}>Subtotal</th>
                <th style={{ width: 40 }}></th>
              </tr>
            </thead>
            <tbody>
              {lista.map((it) => {
                const idxGlobal = itens.indexOf(it);
                const estoqueAviso = direcao === 'levado' && it.estoque_atual != null && it.quantidade > it.estoque_atual;
                return (
                  <tr key={`${it.direcao}-${it.produto_id}`} style={estoqueAviso ? { background: 'rgba(220,38,38,0.06)' } : undefined}>
                    <td style={{ width: 64, padding: '2px 4px', height: 1 }}>
                      {it.foto_url ? (
                        <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <img src={it.foto_url} alt="" style={{ maxHeight: '100%', maxWidth: 60, objectFit: 'contain', display: 'block' }} />
                        </div>
                      ) : null}
                    </td>
                    <td className="text-gray-500 font-bold no-underline">{it.codigo_interno || '-'}</td>
                    <td>
                      {it.descricao}
                      {estoqueAviso && (
                        <span style={{ marginLeft: 6, color: '#dc2626', fontSize: 11 }}>
                          <AlertTriangle size={11} style={{ display: 'inline', marginRight: 2 }} />
                          estoque: {it.estoque_atual}
                        </span>
                      )}
                    </td>
                    <td style={{ textAlign: 'right' }}>{formatMoeda(it.preco_unitario)}</td>
                    <td style={{ textAlign: 'center' }}>
                      <input
                        type="number"
                        min="0.001"
                        step="0.001"
                        value={it.quantidade}
                        onChange={(e) => mudarQtd(idxGlobal, Number(e.target.value))}
                        style={{ width: 70, textAlign: 'center', padding: '2px 4px' }}
                      />
                    </td>
                    <td style={{ textAlign: 'right', fontWeight: 600 }}>{formatMoeda(it.subtotal)}</td>
                    <td>
                      <button type="button" className="btn-icon" onClick={() => removerItem(idxGlobal)} title="Remover">
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        </div>
      </div>
    );
  }

  // Compra com Credito: Orcamento (combo dos orcamentos abertos do cliente) a esquerda, Credito da
  // Loja (os creditos de troca em nome dele) a direita. Observacoes e o Finalizar sao os da tela.
  function renderCompra() {
    const cartao: CSSProperties = {
      background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 8, padding: 16,
      display: 'flex', flexDirection: 'column', minHeight: 0,
    };
    // Rodape fixo dos dois quadros (pedido dele, 22/10/2026): as mesmas cores do cabecalho da tabela,
    // par casado de tema (--grid-header-bg/-fg), fonte em negrito. Fora da area que rola.
    const rodape: CSSProperties = {
      flexShrink: 0, marginTop: 8, padding: '10px 16px', borderRadius: 6,
      background: 'var(--grid-header-bg)', color: 'var(--grid-header-fg)',
      display: 'flex', justifyContent: 'flex-end', alignItems: 'baseline', gap: 32,
      fontSize: 15, fontWeight: 700, minHeight: 44,
    };
    const opcoes = orcamentosCompra.map((o) => ({
      value: o.id,
      label: `${o.numero} — ${o.cliente_nome} — ${formatMoeda(Number(o.total))} — ${formatDataHoraSP(o.criado_em)}${o.vendedor_nome ? ` — ${o.vendedor_nome}` : ''}`,
    }));
    return (
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(440px, 1fr))',
        gap: 12, marginBottom: 12, flex: 1, minHeight: 0,
      }}>
        <div style={cartao}>
          {/* Sem titulo "Orcamento": o combo ja diz o que e' (pedido dele, 22/10/2026: ganhar uma linha). */}
          <div style={{ flexShrink: 0, marginBottom: 10 }}>
            <ComboBox
              opcoes={opcoes}
              value={orcSelId}
              onChange={(v) => setOrcSelId(v === '' ? null : Number(v))}
              placeholder={carregandoCompra ? 'Carregando...' : 'Escolha o orcamento (numero ou cliente)...'}
              disabled={carregandoCompra || orcamentosCompra.length === 0}
            />
          </div>
          {/* Sem a dica e sem a linha do cliente (pedido dele, 22/10/2026): o nome ja esta no combo, e
              lista vazia continua explicada na faixa amarela de bloqueio. */}
          <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
            {!orcSel ? (
              <div style={{ padding: 16, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
                Os itens do orcamento aparecem aqui
              </div>
            ) : (
              <table className="tabela" style={{ width: '100%', fontSize: 14 }}>
                <thead>
                  <tr>
                    <th style={{ width: 64 }}></th>
                    <th style={{ width: 70 }}>Cod</th>
                    <th>Produto</th>
                    <th style={{ width: 70, textAlign: 'right' }}>Qtd</th>
                    <th style={{ width: 100, textAlign: 'right' }}>Unit.</th>
                    <th style={{ width: 110, textAlign: 'right' }}>Subtotal</th>
                  </tr>
                </thead>
                <tbody>
                  {itensSel.map((i, idx) => {
                    const falta = Number(i.estoque_atual) < Number(i.quantidade);
                    return (
                      <tr key={`${i.produto_id}-${idx}`} style={falta ? { background: 'rgba(220,38,38,0.06)' } : undefined}>
                        {/* Foto do produto — mesma celula das listas de Devolvidos/Levados (pedido dele, 22/10/2026). */}
                        <td style={{ width: 64, padding: '2px 4px' }}>
                          {i.foto_url ? (
                            <img src={i.foto_url} alt="" style={{ maxHeight: 44, maxWidth: 60, objectFit: 'contain', display: 'block', margin: '0 auto' }} />
                          ) : null}
                        </td>
                        <td className="text-gray-500 font-bold no-underline">{i.codigo_interno || '-'}</td>
                        <td>
                          {i.produto_descricao}
                          {falta && (
                            <span style={{ marginLeft: 6, color: '#dc2626', fontSize: 11 }}>
                              <AlertTriangle size={11} style={{ display: 'inline', marginRight: 2 }} />
                              estoque: {Number(i.estoque_atual)}
                            </span>
                          )}
                        </td>
                        <td style={{ textAlign: 'right' }}>{Number(i.quantidade) % 1 === 0 ? Number(i.quantidade) : Number(i.quantidade).toFixed(3)}</td>
                        <td style={{ textAlign: 'right' }}>{formatMoeda(Number(i.preco_unitario))}</td>
                        <td style={{ textAlign: 'right', fontWeight: 600 }}>{formatMoeda(Number(i.subtotal))}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
          {/* Total FORA da area que rola: sempre visivel, por mais itens que o orcamento tenha (pedido dele). */}
          <div style={rodape}>
            <span>Total do orcamento: <span style={{ marginLeft: 12 }}>{formatMoeda(totalCompra)}</span></span>
          </div>
        </div>

        <div style={cartao}>
          <h3 style={{ margin: '0 0 10px 0', fontSize: 16, fontWeight: 700, color: '#16a34a' }}>Credito da Loja</h3>
          <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
            {creditosDoCliente.length === 0 ? (
              <div style={{ padding: 16, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
                Os creditos do cliente do orcamento aparecem aqui
              </div>
            ) : (
              <table className="tabela" style={{ width: '100%', fontSize: 14 }}>
                <thead>
                  <tr>
                    <th>Credito</th>
                    <th>Origem</th>
                    <th style={{ width: 140 }}>Data</th>
                    <th style={{ width: 110, textAlign: 'right' }}>Saldo</th>
                  </tr>
                </thead>
                <tbody>
                  {creditosDoCliente.map((c) => (
                    <tr key={c.id}>
                      <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{c.numero}</td>
                      <td style={{ fontSize: 12 }}>{c.troca_numero ?? '—'}</td>
                      <td style={{ fontSize: 12 }}>{formatDataHoraSP(c.criado_em)}</td>
                      <td style={{ textAlign: 'right', fontWeight: 600 }}>{formatMoeda(Number(c.saldo))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          <div style={rodape}>
            <span>Credito disponivel: <span style={{ marginLeft: 12 }}>{formatMoeda(creditoDisponivel)}</span></span>
            <span>Sobra depois da compra: <span style={{ marginLeft: 12 }}>{orcSel && sobraCredito >= 0 ? formatMoeda(sobraCredito) : '—'}</span></span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      {/* Faixa do cliente. As opcoes Devolucao/Troca/Compra subiram pro cabecalho e, na Compra com
          Credito, a faixa some — o cliente vem do orcamento (pedidos dele, 22/10/2026: ganhar altura). */}
      {!ehCompra && (
      <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 8, padding: 12, marginBottom: 12, display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'flex-start', flexShrink: 0 }}>
        <div style={{ flex: 1, minWidth: 280 }}>
          {cliente ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>
                {cliente.codigo_legado ? `Cod ${String(cliente.codigo_legado).padStart(4, '0')}` : `ID ${cliente.id}`}
              </span>
              <strong>{cliente.nome}</strong>
              <button type="button" className="btn-secundario" onClick={() => setShowModalCliente(true)}>Trocar</button>
              <button type="button" className="btn-icon" onClick={() => setCliente(null)} title="Limpar">
                <XIcon size={14} />
              </button>
              {cliente.cpf_cnpj && (
                <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>· {cliente.cpf_cnpj}</span>
              )}
            </div>
          ) : (
            <button type="button" className="btn-primario" onClick={() => setShowModalCliente(true)}>
              <UserPlus size={14} style={{ display: 'inline', marginRight: 4 }} />
              Selecionar cliente
            </button>
          )}
        </div>
      </div>
      )}

      {ehCompra ? renderCompra() : (
      /* Itens — lado a lado em troca; coluna unica em devolucao. Miolo que rola (tela cabe sem scroll). */
      <div style={{
        display: 'grid',
        gridTemplateColumns: tipo === 'troca' ? 'repeat(auto-fit, minmax(440px, 1fr))' : '1fr',
        gap: 12,
        marginBottom: 12,
        flex: 1,
        minHeight: 0,
      }}>
        {renderSecao('devolvido', true)}
        {renderSecao('levado', tipo === 'troca')}
      </div>
      )}

      {/* Rodape: observacoes (1 linha, largura total) */}
      <div style={{
        background: '#fff', border: '1px solid var(--border)', borderRadius: 8,
        padding: '6px 10px', marginBottom: 0, flexShrink: 0,
      }}>
        <input
          type="text"
          value={observacoes}
          onChange={(e) => setObservacoes(upperMantendoCursor(e))}
          maxLength={2000}
          placeholder="Observacoes"
          style={{
            width: '100%', border: 'none', outline: 'none', background: 'transparent',
            fontSize: 14, padding: 4,
          }}
        />
      </div>

      {/* Compra com credito: o motivo do Finalizar apagado fica escrito na tela, nao so no tooltip. */}
      {ehCompra && bloqueio && !carregandoCompra && (orcSel || orcamentosCompra.length === 0 || erroCompra) && (
        <div style={{ padding: 8, marginTop: 8, background: '#fde68a', color: '#000', borderRadius: 6, fontSize: 13, fontWeight: 600, flexShrink: 0 }}>
          <AlertTriangle size={14} style={{ display: 'inline', marginRight: 4, marginBottom: -2 }} />
          {bloqueio}
        </div>
      )}

      {/* Aviso quando saldo e negativo (cliente paga diferenca no caixa) */}
      {!ehCompra && saldoCredito < 0 && (
        <div style={{ padding: 8, marginBottom: 12, background: 'rgba(220,38,38,0.08)', color: '#dc2626', border: '1px solid #dc2626', borderRadius: 6, fontSize: 13 }}>
          <AlertTriangle size={14} style={{ display: 'inline', marginRight: 4, marginBottom: -2 }} />
          Cliente vai pagar a diferenca de <strong>{formatMoeda(Math.abs(saldoCredito))}</strong> no caixa. Registre venda complementar dos itens levados separadamente.
        </div>
      )}

      {showModalCliente && (
        <ModalBuscarCliente
          onSelect={(c) => {
            setCliente({ id: c.id, nome: c.nome, codigo_legado: c.codigo_legado, cpf_cnpj: c.cpf_cnpj });
            setShowModalCliente(false);
          }}
          onClose={() => setShowModalCliente(false)}
        />
      )}

      {reciboCompra && (
        <ModalReciboTroca
          recibo={reciboCompra}
          onFechar={() => {
            setReciboCompra(null);
            limpar();
            onConcluida();
          }}
        />
      )}
    </div>
  );
});
